import type { BookData } from '../interfaces/book-data.interface';
import { denormalize } from './denormalize';
import { normalize } from './normalize';
import type { PersistEncodeMode } from './persist-gate';
import { sweepRosterFrameRoles } from './roster-frame-role-sweep';
import { detectSchemaVersion, type DetectedSchema } from './schema-detect';
import type { NormalizeIssue, PersistedBookModel, PersistedBookModelV1, SchemaVersion } from './types';

/** Browser-console sink — a NestJS caller must pass its own Logger-backed `onIssue`, not rely on this default. */
function reportNormalizeIssueToConsole(issue: NormalizeIssue): void {
  console.warn(`[photobook-model] ${issue.severity} at ${issue.path}: ${issue.reason}`);
}

/**
 * `raw` is whatever the server currently has in `photobook.model` (legacy or v1) — `denormalize`
 * already resolves the schema and passes a legacy document through by the same reference.
 */
export function decodePersistedBookModel(
  raw: unknown,
  onIssue: (issue: NormalizeIssue) => void = reportNormalizeIssueToConsole,
): BookData | null {
  if (raw === null || raw === undefined) return null;

  const { model, issues } = denormalize(raw);
  issues.forEach(onIssue);
  return model;
}

/**
 * Decode that STOPS at v1: a v1 document passes through by the same reference, a legacy one is
 * upgraded via `normalize`, and a legacy one `normalize` cannot represent throws
 * (`NormalizerFatalError` — a silent partial decode would corrupt the model on the next save).
 *
 * Passing a v1 document through by reference means it is NOT re-validated per box (`rectFromBox`
 * inside `denormalize` never runs for it) — that trade is the point of this entry. It also leaves
 * routing a MIXED document (`isMixedShape`) to the caller: detection judges such a book by its
 * first collage and calls the whole thing v1, so its remaining collages would read `boxes: undefined`.
 *
 * `detected` lets a caller that already ran {@link detectSchemaVersion} (to make exactly that routing
 * decision) hand the result in instead of paying for a second walk over every collage.
 *
 * The one exception to passing a v1 document through untouched is {@link sweepRosterFrameRoles}: the
 * roster-cell gate has to run on read as well, or a document that arrived past the UI would carry a
 * frame role all the way to print. It is copy-on-write — a clean document still comes back by reference.
 */
export function decodePersistedBookModelV1(
  raw: unknown,
  onIssue: (issue: NormalizeIssue) => void = reportNormalizeIssueToConsole,
  detected?: DetectedSchema,
): PersistedBookModelV1 | null {
  if (raw === null || raw === undefined) return null;

  const schema = detected ?? detectSchemaVersion(raw);
  schema.issues.forEach(onIssue);
  if (schema.schemaVersion === 'custom-template-v1') return sweepRosterFrameRoles(raw as PersistedBookModelV1, onIssue);

  const { model, issues } = normalize(raw as BookData);
  issues.forEach(onIssue);
  return model;
}

/** Emitted once per encode so every writer can log the schemaVersion share during the v1 rollout. */
export interface PersistEncodeReport {
  readonly schemaVersion: SchemaVersion;
  /** True when a v1 encode failed and the legacy shape was persisted instead. */
  readonly fallback: boolean;
  /**
   * Why the fallback happened — present only when `fallback` is true. The report is the single sink a
   * caller needs to raise the alarm: without it a writer had to re-derive the cause from a `drop` issue,
   * and the one silent way legacy is still born under an open gate stayed at warn level.
   */
  readonly fallbackReason?: string;
}

export interface PersistEncodeOptions {
  readonly mode?: PersistEncodeMode;
  readonly onIssue?: (issue: NormalizeIssue) => void;
  readonly onEncoded?: (report: PersistEncodeReport) => void;
}

/** Default mode is `identity` — the persist gate stays closed unless the caller resolved it open. */
export function encodeBookModelForPersist(model: BookData, options: PersistEncodeOptions = {}): PersistedBookModel {
  // ADR-0018: a vignette cell may only carry a personId/peopleType reference, never a photo — this
  // shared codec is not the trust boundary for that invariant (both FE and a compromised/buggy client
  // run it); the enforcement point is the server write-gate (core-service PhotobookModelWriteGateService).
  const { mode = 'identity', onIssue = reportNormalizeIssueToConsole, onEncoded } = options;
  if (mode === 'identity') {
    onEncoded?.({ schemaVersion: 'legacy-drawable-v0', fallback: false });
    return model;
  }

  try {
    const { model: encoded, issues } = normalize(model);
    issues.forEach(onIssue);
    onEncoded?.({ schemaVersion: 'custom-template-v1', fallback: false });
    return encoded;
  } catch (error) {
    // A save must not be lost to an encode bug: the legacy shape is always decodable, so persist it and report loudly.
    const message = error instanceof Error ? error.message : String(error);
    onIssue({
      severity: 'drop',
      path: 'persist-encode-gate',
      reason: `custom-template-v1 encode failed, persisted legacy shape instead: ${message}`,
    });
    onEncoded?.({ schemaVersion: 'legacy-drawable-v0', fallback: true, fallbackReason: message });
    return model;
  }
}
